import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Phone, Mail, MapPin, Clock, Quote, Star,
  Sun, Moon, Plus, Minus,
} from 'lucide-react';
import useDemoFonts from '../../hooks/useDemoFonts';

// Pure rendering of a small-business demo. Source-agnostic — works for the
// baked-in sample demos at /demos/:slug AND token-based prospect demos at
// /p/:token. Theme is data-driven via the `palettes` (light + dark) and
// `fontDisplay` fields. Single-`palette` demos still render (one mode only).
//
// Props:
//   demo:    the demo data (matches src/data/demos.js shape)
//   sample:  true for showcase demos; renders a "Sample design" badge
const MODE_KEY = 'sudco-demo-mode';

function DemoTemplate({ demo, sample = false }) {
  useDemoFonts();
  const palettes = useMemo(() => resolvePalettes(demo), [demo]);
  const hasDarkMode = palettes.light !== palettes.dark;

  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return demo.defaultMode || 'light';
    const stored = window.localStorage.getItem(MODE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return demo.defaultMode || 'light';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MODE_KEY, mode);
    }
  }, [mode]);

  const p = palettes[mode] || palettes.light;
  const isDark = isLight(p.bg) === false;

  const styles = {
    page: { background: p.bg, color: p.text, fontFamily: 'Inter, system-ui, sans-serif' },
    display: { fontFamily: demo.fontDisplay || 'Inter, system-ui, sans-serif' },
    accent: { color: p.accent },
    accentBg: { background: p.accent, color: '#fff' },
    accentBorder: { borderColor: p.accent },
    surface: { background: p.surface, borderColor: hexAlpha(p.text, 0.08) },
    muted: { color: p.muted },
    divider: { borderColor: hexAlpha(p.text, 0.08) },
    softBg: { background: hexAlpha(p.text, isDark ? 0.04 : 0.025) },
  };

  const testimonials = demo.testimonials?.length
    ? demo.testimonials
    : (demo.testimonial ? [demo.testimonial] : []);

  return (
    <div style={styles.page} className="min-h-screen transition-colors duration-300">
      <div className="fixed bottom-5 right-5 z-50">
        <div className="bg-bg/90 border border-border backdrop-blur-md rounded-full px-5 py-3 shadow-glow flex items-center gap-4 text-sm">
          <span className="text-text-muted hidden sm:inline">
            {sample
              ? <>Like this? Get one for your <span className="text-text font-medium">{demo.industry}</span> business — <span className="text-accent font-semibold">$800</span></>
              : <>Free demo for <span className="text-text font-medium">{demo.name}</span> by <span className="text-accent font-semibold">Sudco Solutions</span></>
            }
          </span>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-text font-semibold hover:text-accent-2 transition-colors whitespace-nowrap"
          >
            <ArrowLeft size={14} /> Back to Sudco
          </Link>
        </div>
      </div>

      <header className="sticky top-0 z-30 backdrop-blur-md transition-colors duration-300" style={{ background: hexAlpha(p.bg, 0.85), borderBottom: `1px solid ${hexAlpha(p.text, 0.08)}` }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div style={styles.display} className="font-bold text-xl tracking-tight">{demo.name}</div>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#services" className="hover:opacity-70" style={{ color: p.text }}>Services</a>
            {demo.featured?.items?.length ? (
              <a href="#featured" className="hover:opacity-70" style={{ color: p.text }}>{demo.featured.navLabel || 'Menu'}</a>
            ) : null}
            {demo.gallery?.length ? <a href="#gallery" className="hover:opacity-70" style={{ color: p.text }}>Gallery</a> : null}
            <a href="#hours" className="hover:opacity-70" style={{ color: p.text }}>Hours</a>
            <a href="#contact" className="hover:opacity-70" style={{ color: p.text }}>Contact</a>
            {hasDarkMode && (
              <button
                type="button"
                onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
                aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
                className="w-9 h-9 rounded-full flex items-center justify-center border transition-colors"
                style={{ borderColor: hexAlpha(p.text, 0.15), color: p.text }}
              >
                {mode === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
              </button>
            )}
            <a href="#contact" className="px-4 py-2 rounded-full text-sm font-semibold" style={styles.accentBg}>
              Get in touch
            </a>
          </nav>
          {hasDarkMode && (
            <button
              type="button"
              onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
              aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
              className="md:hidden w-9 h-9 rounded-full flex items-center justify-center border"
              style={{ borderColor: hexAlpha(p.text, 0.15), color: p.text }}
            >
              {mode === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>
          )}
        </div>
      </header>

      <section className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img src={demo.cover} alt="" width="1600" height="900" fetchpriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${hexAlpha(p.bg, 0.4)} 0%, ${hexAlpha(p.bg, 0.9)} 100%)` }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-28 md:py-40">
          <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-5" style={{ background: hexAlpha(p.accent, 0.15), color: p.accent }}>
            <MapPin size={12} className="inline mr-1 -mt-0.5" />
            {demo.location}
          </div>
          <h1 style={styles.display} className="font-bold text-4xl md:text-6xl mb-5 max-w-3xl tracking-tight leading-[1.1]">
            {demo.name}
          </h1>
          <p className="text-lg md:text-xl max-w-2xl leading-relaxed mb-8" style={styles.muted}>
            {demo.tagline}
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#contact" className="px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2" style={styles.accentBg}>
              Get in touch <ArrowRight size={16} />
            </a>
            <a href="#services" className="px-6 py-3 rounded-full font-semibold border" style={{ borderColor: hexAlpha(p.text, 0.2), color: p.text }}>
              Our services
            </a>
          </div>
        </div>
      </section>

      {demo.stats?.length ? (
        <section className="py-12 border-y" style={{ ...styles.softBg, borderColor: hexAlpha(p.text, 0.06) }}>
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {demo.stats.map((s) => (
              <div key={s.label} className="text-center md:text-left">
                <div style={{ ...styles.display, color: p.accent }} className="text-3xl md:text-4xl font-bold tracking-tight">{s.value}</div>
                <div style={styles.muted} className="text-xs uppercase tracking-widest mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section id="services" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>What we do</p>
            <h2 style={styles.display} className="font-bold text-3xl md:text-4xl mb-3">Services</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {demo.services.map((s) => (
              <div key={s.title} className="p-6 rounded-2xl border" style={styles.surface}>
                <div className="w-10 h-1 rounded-full mb-4" style={styles.accentBg} />
                <h3 style={styles.display} className="font-bold text-xl mb-2">{s.title}</h3>
                <p style={styles.muted} className="text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {demo.about ? (
        <section className="py-20" style={styles.softBg}>
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>{demo.about.kicker || 'About'}</p>
              <h2 style={styles.display} className="font-bold text-3xl md:text-4xl mb-5">{demo.about.heading || 'Our story'}</h2>
              {demo.about.body.split(/\n\n+/).map((para, i) => (
                <p key={i} className="leading-relaxed mb-4" style={styles.muted}>{para}</p>
              ))}
              {demo.about.signature ? (
                <p style={{ ...styles.display, color: p.text }} className="mt-6 text-sm font-semibold">— {demo.about.signature}</p>
              ) : null}
            </div>
            {(demo.about.image || demo.gallery?.[0]) ? (
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden order-first md:order-last">
                <img src={demo.about.image || demo.gallery[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {demo.featured?.items?.length ? (
        <section id="featured" className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>{demo.featured.kicker || 'Featured'}</p>
              <h2 style={styles.display} className="font-bold text-3xl md:text-4xl mb-3">{demo.featured.heading}</h2>
              {demo.featured.subheading ? (
                <p style={styles.muted} className="leading-relaxed">{demo.featured.subheading}</p>
              ) : null}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {demo.featured.items.map((item) => (
                <div key={item.name} className="p-6 rounded-2xl border flex flex-col" style={styles.surface}>
                  <div className="flex justify-between items-start mb-2 gap-3">
                    <h3 style={styles.display} className="font-bold text-lg leading-tight">{item.name}</h3>
                    {item.price ? (
                      <span className="font-bold text-sm whitespace-nowrap" style={styles.accent}>{item.price}</span>
                    ) : null}
                  </div>
                  {item.desc ? (
                    <p style={styles.muted} className="text-sm leading-relaxed">{item.desc}</p>
                  ) : null}
                  {item.tag ? (
                    <span className="inline-block mt-4 px-2 py-0.5 rounded-full text-xs font-semibold self-start" style={{ background: hexAlpha(p.accent2 || p.accent, 0.15), color: p.accent2 || p.accent }}>
                      {item.tag}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {demo.gallery?.length ? (
        <section id="gallery" className="py-20" style={styles.softBg}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>Gallery</p>
                <h2 style={styles.display} className="font-bold text-3xl md:text-4xl">A look inside</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {demo.gallery.map((src, i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl bg-black/10 aspect-square">
                  <img src={src} alt="" width="900" height="900" loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {testimonials.length === 1 ? (
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <Quote size={32} style={styles.accent} className="mx-auto mb-5 opacity-60" />
            <p style={styles.display} className="text-2xl md:text-3xl font-semibold leading-snug mb-5">
              "{testimonials[0].quote}"
            </p>
            <p style={styles.muted} className="text-sm">— {testimonials[0].author}</p>
          </div>
        </section>
      ) : testimonials.length > 1 ? (
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>Reviews</p>
              <h2 style={styles.display} className="font-bold text-3xl md:text-4xl">What folks are saying</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-6 rounded-2xl border flex flex-col" style={styles.surface}>
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => (
                      <Star key={j} size={14} fill={p.accent} stroke={p.accent} />
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed mb-4 flex-grow">"{t.quote}"</p>
                  <p style={styles.muted} className="text-xs">— {t.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {demo.faq?.length ? (
        <section className="py-20" style={styles.softBg}>
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>FAQ</p>
              <h2 style={styles.display} className="font-bold text-3xl md:text-4xl">Common questions</h2>
            </div>
            <div className="space-y-3">
              {demo.faq.map((item, i) => (
                <FaqItem key={i} q={item.q} a={item.a} styles={styles} accent={p.accent} />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section id="contact" className="py-20" style={{ background: hexAlpha(p.accent, 0.06) }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10">
            <div id="hours">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>Hours</p>
              <h2 style={styles.display} className="font-bold text-3xl md:text-4xl mb-6">When we're open</h2>
              <ul className="space-y-3">
                {demo.hours.map((h) => (
                  <li key={h.day} className="flex justify-between items-center py-3 border-b" style={styles.divider}>
                    <span className="flex items-center gap-2 font-medium"><Clock size={16} style={styles.accent} />{h.day}</span>
                    <span style={styles.muted}>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={styles.accent}>Get in touch</p>
              <h2 style={styles.display} className="font-bold text-3xl md:text-4xl mb-6">Reach out</h2>
              <div className="space-y-3 mb-7">
                <a href={`tel:${demo.contact.phone}`} className="flex items-center gap-3 hover:opacity-80">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: hexAlpha(p.accent, 0.15) }}>
                    <Phone size={16} style={styles.accent} />
                  </span>
                  <span>{demo.contact.phone}</span>
                </a>
                <a href={`mailto:${demo.contact.email}`} className="flex items-center gap-3 hover:opacity-80">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: hexAlpha(p.accent, 0.15) }}>
                    <Mail size={16} style={styles.accent} />
                  </span>
                  <span>{demo.contact.email}</span>
                </a>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: hexAlpha(p.accent, 0.15) }}>
                    <MapPin size={16} style={styles.accent} />
                  </span>
                  <span>{demo.contact.address}</span>
                </div>
              </div>
              <a href={`mailto:${demo.contact.email}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" style={styles.accentBg}>
                Send us a message <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-10 border-t" style={styles.divider}>
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm" style={styles.muted}>
          <p>&copy; {new Date().getFullYear()} {demo.name}.</p>
          <p>
            Demo site built by{' '}
            <Link to="/" className="font-semibold" style={styles.accent}>Sudco Solutions</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

function FaqItem({ q, a, styles, accent }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border overflow-hidden" style={styles.surface}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left p-5 flex justify-between items-center gap-4"
      >
        <span style={styles.display} className="font-semibold text-base md:text-lg">{q}</span>
        {open ? <Minus size={18} style={{ color: accent, flexShrink: 0 }} /> : <Plus size={18} style={{ color: accent, flexShrink: 0 }} />}
      </button>
      {open ? (
        <div className="px-5 pb-5 text-sm leading-relaxed" style={styles.muted}>{a}</div>
      ) : null}
    </div>
  );
}

function resolvePalettes(demo) {
  if (demo.palettes?.light && demo.palettes?.dark) return demo.palettes;
  return { light: demo.palette, dark: demo.palette };
}

function hexAlpha(hex, alpha) {
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function isLight(hex) {
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 > 140;
}

export default DemoTemplate;
